import React from 'react';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Tooltip } from 'ui/tooltip';
import { AddNew16 } from 'ui/icons/aurora/16px';
import { Skeleton } from 'ui/skeleton';
import { PageHeader } from 'ui/pageHeader';
import { PrimaryButton } from 'ui/button';
import { ErrorPage } from 'core/errorPage';
import isEmpty from 'lodash/isEmpty';
import { ORG_PERMISSIONS } from 'utils/permissions';
import {
  selectOrganizationId,
  selectIsBossOrSalesBossLevel,
  selectDoesUserHaveRbacPermission,
  selectDoesUserHaveRbacPermissionByProject,
  selectUserOrgAdmin,
} from 'core/userContext/selectors';
import {
  selectCombinedRoles,
  getOrganizationProjectById,
  getOrganizationProjects,
  selectOrganizationMembers,
  selectProjectRoles,
  getOrganizationMembersLoading,
  getOrganizationProjectsLoading,
  getOrganizationRolesLoading,
  selectTeamRoles,
  selectOrganizationPermissionsForForm,
} from '../selectors';
import {
  fetchOrganizationMembersRequest,
  fetchOrganizationProjectsRequest,
  fetchOrganizationRolesRequest,
  fetchPermissionsRequest,
  updateOrganizationMemberRequest,
  updateOrganizationMembershipRequest,
  createOrganizationMemberRequest,
} from '../actions';
import { CreateMemberModal } from './CreateMemberModal';
import ProjectMembers from './ProjectMembers';
// import type { MemberQuery, Member } from '../types';
import { ProjectHeaderDL } from './ProjectMembersPage.styled';
import { getMembersWithProjectInfo } from './ProjectMembersPage.utils';

// type Props = {
//   canManageOrgMembers: Boolean,
//   canManageProject: ?() => Boolean,
//   combinedRoles: Object,
//   projectId: number,
//   match: object,
//   project: Project,
//   projects: Project[],
//   isLoading: Boolean,
//   projectRoles: object,
//   teamRoles: object,
//   fetchOrganizationMembersRequest: (query: MemberQuery) => void,
//   members: Member[],
//   fetchOrganizationProjectsRequest: ?() => {},
//   fetchOrganizationMembersRequest: ?() => {},
//   fetchOrganizationRolesRequest: ?() => {},
//   fetchPermissionsRequest: ?() => {},
//   updateOrganizationMemberRequest: ?() => {},
//   updateOrganizationMembershipRequest: ?() => {},
//   createOrganizationMemberRequest: ?() => {},
//   orgId: number,
//   isBaus: Boolean,
//   organizationPermissionsForForm: [],
//   isUserOrgAdmin: boolean,
// };

export class ProjectMembersPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { createMemberModalIsOpen: false };
  }

  openCreateMemberModal = () => {
    this.setState({
      createMemberModalIsOpen: true,
    });
  };

  closeCreateMemberModal = () => {
    this.setState({
      createMemberModalIsOpen: false,
    });
  };

  componentDidMount() {
    this.props.fetchOrganizationMembersRequest({});
    this.props.fetchOrganizationProjectsRequest({});
    this.props.fetchOrganizationRolesRequest();
    this.props.fetchPermissionsRequest();
  }

  onSave = (updatedMembers) => {
    updatedMembers.map((member) => this.props.updateOrganizationMembershipRequest(member, true));
  };

  membersWithProjectInfo = () => getMembersWithProjectInfo(this.props);
  render() {
    const {
      combinedRoles,
      projects,
      project,
      members,
      projectRoles,
      teamRoles,
      isLoading,
      orgId,
      isBaus,
      canManageOrgMembers,
      canManageProject,
      organizationPermissionsForForm,
      isUserOrgAdmin,
    } = this.props;

    const dataReady = !(
      isEmpty(projects) ||
      isEmpty(project) ||
      isEmpty(members) ||
      isEmpty(projectRoles) ||
      isEmpty(teamRoles)
    );

    if (!dataReady && isLoading) {
      return <Skeleton height={800} />;
    } else if (!project) {
      return (
        <ErrorPage
          backText={`Back to Organization Members List`}
          backTo={`/members`}
          header={`Project Not Found`}
        />
      );
    }

    const headerButtons = (
      <Tooltip
        isEnabled={!canManageOrgMembers}
        tooltipContent={
          'You do not have permissions to manage and set roles for member accounts in this organization. An organization administrator could change this.'
        }
      >
        <PrimaryButton isDisabled={!canManageOrgMembers} onClick={this.openCreateMemberModal}>
          <AddNew16 />
          Add a new member
        </PrimaryButton>
      </Tooltip>
    );

    return (
      <>
        {this.state.createMemberModalIsOpen && (
          <CreateMemberModal
            closeCreateMemberModal={this.closeCreateMemberModal}
            onCreate={this.props.createOrganizationMemberRequest}
            organizationPermissionsForForm={organizationPermissionsForForm}
            isUserOrgAdmin={isUserOrgAdmin}
          />
        )}
        <PageHeader pageActions={headerButtons}>Members of {project.name}</PageHeader>
        <ProjectHeaderDL>
          <dt>Org ID:</dt>
          <dd>{orgId}</dd>
          <dt>Project ID:</dt>
          <dd>{project.id}</dd>
          {isBaus && (
            <>
              <dt>ES Index:</dt>
              <dd>{project.index}</dd>
            </>
          )}
        </ProjectHeaderDL>
        <ProjectMembers
          canManageOrgMembers={canManageOrgMembers}
          canManageProject={canManageProject}
          project={project}
          projects={projects}
          members={this.membersWithProjectInfo()}
          combinedRoleOptions={combinedRoles}
          projectRoleOptions={projectRoles}
          teamRoleOptions={teamRoles}
          onSave={this.onSave}
        />
      </>
    );
  }
}

const mapStateToProps = (state, props) => ({
  combinedRoles: selectCombinedRoles(state),
  isLoading:
    getOrganizationMembersLoading(state) ||
    getOrganizationProjectsLoading(state) ||
    getOrganizationRolesLoading(state),
  project: getOrganizationProjectById(state, { projectId: props.match.params.projectId }),
  projects: getOrganizationProjects(state, props),
  members: selectOrganizationMembers(state, props),
  projectRoles: selectProjectRoles(state),
  teamRoles: selectTeamRoles(state),
  orgId: selectOrganizationId(state),
  isBaus: selectIsBossOrSalesBossLevel(state),
  canManageOrgMembers: selectDoesUserHaveRbacPermission(state)(ORG_PERMISSIONS.ManageMembers),
  canManageProject: selectDoesUserHaveRbacPermissionByProject(state),
  organizationPermissionsForForm: selectOrganizationPermissionsForForm(state),
  isUserOrgAdmin: selectUserOrgAdmin(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchOrganizationMembersRequest,
      fetchOrganizationProjectsRequest,
      fetchOrganizationRolesRequest,
      fetchPermissionsRequest,
      updateOrganizationMemberRequest,
      updateOrganizationMembershipRequest,
      createOrganizationMemberRequest,
    },
    dispatch,
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectMembersPage));
